import { z } from 'zod';

const trimmedString = () => z.string().trim().min(1);

export const gaSchema = z.object({
	ga_api_secret: trimmedString().optional(),
	ga_measurement_id: trimmedString(),
});

export const metaPixelSchema = z.object({
	meta_pixel_id: trimmedString(),
	meta_access_token: trimmedString(),
});

export const omnisendSchema = z.object({
	brand_id: trimmedString(),
});

export const moengageSchema = z.object({
	api_key: trimmedString(),
	cluster: trimmedString(),
	environment: trimmedString(),
});

export const gtmSchema = z.object({
	container_id: trimmedString(),
});

export const mailchimpSchema = z.object({
	api_key: trimmedString(),
	audience_id: trimmedString(),
});

export const upfluenceSchema = z.object({
	id: trimmedString(),
});

export const shortioSchema = z.object({
	enabled: z.boolean(),
});

export const inboundMarketingSchema = z.object({
	ga: gaSchema.optional(),
	meta_pixel: metaPixelSchema.optional(),
	omnisend: omnisendSchema.optional(),
	moengage: moengageSchema.optional(),
	gtm: gtmSchema.optional(),
	mailchimp: mailchimpSchema.optional(),
	upfluence: upfluenceSchema.optional(),
	shortio: shortioSchema.optional(),
});

export type InboundMarketing = z.infer<typeof inboundMarketingSchema>;

const isValidObject = <T extends Record<string, any>>(
	obj: T | null | undefined,
): obj is T =>
	obj !== null &&
	obj !== undefined &&
	typeof obj === 'object' &&
	Object.keys(obj).length > 0;

export const validateInboundMarketing = <K extends keyof InboundMarketing>(
	property: K,
	marketing: InboundMarketing | null | undefined,
): NonNullable<InboundMarketing[K]> | null => {
	const obj = marketing?.[property];
	if (!isValidObject(obj)) return null;

	const propertySchema = inboundMarketingSchema.shape[property];
	if (!propertySchema) return null;

	const parseResult = propertySchema.safeParse(obj);
	return parseResult.success
		? (parseResult.data as NonNullable<InboundMarketing[K]>)
		: null;
};

export interface AddOns {
	new_arrivals_brands_period: string;
	new_arrivals_drops_period: string;
	new_arrivals_products_period: string;
	'11-11-sale': November_11_AddOn;
	'laam-meta-pixel': {
		enabled: boolean;
	};
	eid_gift?: {
		enabled: boolean;
	};
	wishlist_v2?: {
		enable: boolean;
	};
	'standard-meta-pixel': boolean;
	whatsappFloatingIcon?: boolean;
}

export interface November_11_AddOn {
	enabled?: boolean;
}
